<template>
  <AppLink
    v-if="onlyOneChild.meta"
    :to="routePath"
    :track-id="sidebarTrackId"
    :use-full-path="onlyOneChild.meta.useFullPath"
    class="single-sidebar-item"
    :class="{ 'is-collapsed': isCollapsed }"
  >
    <el-menu-item
      :index="routePath"
      :class="
        `menu-item ${
          isNest ? 'menu-item--without-dropdown' : 'menu-item--with-dropdown'
        }`
      "
    >
      <div class="menu-item__inner">
        <SidebarItemTooltip :item="onlyOneChild" :is-collapsed="isCollapsed" />
        <div
          v-if="onlyOneChild.meta.label && (!isCollapsed || isNest)"
          class="menu-item__label"
        >
          {{ $t(onlyOneChild.meta.label) }}
        </div>
        <div
          v-if="shouldDisplayTag"
          class="single-sidebar-item__notification"
          :class="tagTypeClass"
        >
          {{ $t(onlyOneChild.meta.tag.label) }}
        </div>
        <svg-icon
          v-if="onlyOneChild.meta.isExternal"
          color="#7E82AD"
          class="menu-item__suffix--external-icon"
          icon-class="arrow-out"
          size="16px"
        />
        <div
          v-if="hightLightLabel"
          class="single-sidebar-item__hightlight__count"
        >
          {{ hightLightLabel }}
        </div>
      </div>
    </el-menu-item>
  </AppLink>
</template>

<script>
import { mapState } from 'vuex'
import AppLink from '@/components/SidebarUtils/Link.vue'
import ResolvePathMixin from '@/mixins/resolve-path'
import SidebarItemTooltip from '@/components/SidebarUtils/Tooltip.vue'
import { isFunction } from '@/utils/validate'

export default {
  name: 'SingleSidebarItem',
  components: { SidebarItemTooltip, AppLink },
  mixins: [ResolvePathMixin],
  props: {
    basePath: {
      type: String,
      default: '',
    },
    onlyOneChild: {
      type: Object,
      required: true,
    },
    isNest: {
      type: Boolean,
      default: false,
    },
    isKycRoute: {
      type: Boolean,
      default: false,
    },
    isCollapsed: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('order', ['authorisedOrdersRecords']),
    ...mapState('dispute', [
      'actionRequiredCountSideBar',
      'staticOpenCasesCount',
    ]),
    shouldDisplayHighlightCount() {
      return ['/orders', '/dispute'].includes(this.basePath)
    },
    orderCount() {
      return this.shouldDisplayHighlightCount &&
        this.authorisedOrdersRecords > 99
        ? '99+'
        : this.authorisedOrdersRecords || null
    },
    disputeCount() {
      return this.shouldDisplayHighlightCount &&
        this.actionRequiredCountSideBar > 99
        ? '99+'
        : this.actionRequiredCountSideBar || null
    },
    hightLightLabel() {
      return this.basePath === '/orders'
        ? this.orderCount
        : this.basePath === '/dispute'
        ? this.disputeCount
        : ''
    },
    tagTypeClass() {
      if (!this.shouldDisplayTag) {
        return ''
      }

      return `is-${this.onlyOneChild.meta.tag.type}`
    },
    shouldDisplayTag() {
      if (!this.onlyOneChild?.meta?.tag) {
        return false
      }

      const tag = this.onlyOneChild?.meta?.tag

      if (isFunction(tag.visible)) {
        return tag.visible(this.$store)
      }

      return true
    },
    routePath() {
      if (this.onlyOneChild?.meta?.isExternal) {
        return this.$te(this.basePath) ? this.$t(this.basePath) : this.basePath
      }

      return this.resolvePath(this.onlyOneChild.path)
    },
    sidebarTrackId() {
      // Generate trackId based on the route path
      // e.g., /orders -> sidebar_orders, /dispute -> sidebar_dispute
      const path = this.basePath || this.onlyOneChild.path || ''
      const cleanPath =
        path.replace(/^\//, '').replace(/\//g, '_') || 'sidebar_item'
      return `sidebar_${cleanPath}`
    },
  },
  methods: {
    resolvePath(routePath) {
      return this.resolvePathWithBasePath(this.basePath, routePath)
    },
  },
}
</script>

<style lang="scss">
.single-sidebar-item {
  --single-sidebar-item-width: 100%;
  --single-sidebar-item-height: 100%;

  width: var(--single-sidebar-item-width);
  height: var(--single-sidebar-item-height);

  &__notification {
    --sidebar-item-tag-bg-color: transparent;

    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 4px 8px;
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
    color: var(--warning-text-color);
    text-align: start;
    background-color: var(--tamara-color-system-content-success);
    border-radius: 12px;

    &.is-warning {
      --sidebar-item-tag-bg-color: #5d00ba;
    }

    &.is-success {
      color: var(--tamara-color-content-inverted);
    }

    &.is-info {
      min-width: auto;
      padding: 4px 6px;
      font-weight: 600;
      line-height: 16px;
      color: #167e88;
      text-transform: uppercase;
      background-color: #edfefd;
      border-radius: 8px;
    }
  }

  &__hightlight__count {
    position: relative;
    z-index: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 24px;
    height: 24px;
    padding: 0 6px;
    margin: 0;
    font-size: 12px;
    font-weight: 500;
    color: var(--tamara-color-content-mid);
    text-align: start;
    background-color: var(--tamara-color-background-neutral-high);
    border-radius: 60px;
  }

  &.router-link-active {
    .menu-item {
      .active-menu-icon {
        visibility: visible !important;
        opacity: 1 !important;
      }

      .menu-item__inner {
        color: var(--tamara-color-brand-700);
        border-radius: 5px;
        transition: all 0.3s ease-in;
      }

      .menu-item__label {
        color: var(--tamara-color-brand-700);
      }
    }
  }

  .menu-item {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    padding-inline: 0 !important;
    color: inherit !important;
    background-color: transparent !important;

    &__inner {
      display: flex;
      flex: 1 1 auto;
      align-items: center;
      justify-content: flex-start;
      height: var(--single-sidebar-item-height);
      padding-inline: 0;
      padding-top: 6px;
      padding-bottom: 6px;
      color: var(--tamara-color-content-low);
      transition: background-color 0.3s ease-in-out;

      &:hover {
        border-radius: 5px;
        transition: all 0.3s ease-in;
      }
    }

    &__label {
      flex: 1 1 auto;
      height: 100%;
      max-height: 100%;
      margin-inline-start: 8px;
      font-family: var(--tamara-font-family-primary), sans-serif;
      font-size: 14px;
      font-weight: 600;
      line-height: 24px;
      color: var(--tamara-color-content-low);
      letter-spacing: -0.21px;
    }

    &__suffix--external-icon {
      font-size: 24px;

      [dir='rtl'] & {
        transform: rotate(270deg);
      }
    }

    .active-menu-icon {
      margin-inline-end: 7px;
    }

    &.is-active {
      .menu-item__inner {
        color: var(--tamara-color-brand-700);
      }

      .menu-item__label {
        color: var(--tamara-color-brand-700);
      }

      .single-sidebar-item__hightlight__count {
        color: var(--tamara-color-content-inverted);
        background-color: var(--tamara-color-brand-700);
        transition: all 0.3s ease-in-out;
      }
    }
  }

  &.is-collapsed {
    .menu-item {
      .active-menu-icon {
        display: none;
      }

      .menu-item__inner {
        margin-inline: 0;
      }
    }
  }
}
</style>
